import type { Animation } from '@adflow/types';

/** Stage 1 output: what to render, independent of the editor or WebMCP. */
export type AnimationRenderScene = {
  animation: Animation | null;
  /** Identifies the working copy the frames came from. */
  revision: number;
};

export type FrameSize = { width: number; height: number };

/**
 * Stages 2-3: evaluate the scene at a timestamp and paint a composed frame with no
 * editor overlays. A browser session seeks the off-screen preview; a backend session
 * could evaluate the same animation and paint on a server canvas.
 */
export interface FrameSession {
  readonly size: FrameSize;
  /** Asset problems found while preparing the session (fonts, images). */
  readonly warnings: readonly string[];
  renderFrame(timeSeconds: number, signal?: AbortSignal): Promise<HTMLCanvasElement>;
  close(): Promise<void>;
}

export interface FrameRenderer {
  open(scene: AnimationRenderScene, signal?: AbortSignal): Promise<FrameSession>;
}

export type CreateCanvas = (width: number, height: number) => HTMLCanvasElement;

/** Stage 5 output. Delivery (WebMCP, editor UI, a backend) decides what to do with it. */
export type EncodedArtifact = {
  blob: Blob;
  mimeType: string;
  width: number;
  height: number;
  warnings: string[];
};

export type EncodedVideo = EncodedArtifact & {
  container: 'mp4' | 'webm';
  codec: string | null;
  fps: number;
  durationSeconds: number;
};

export class RenderCancelledError extends Error {
  constructor() {
    super('Rendering was cancelled.');
    this.name = 'RenderCancelledError';
  }
}

export class RenderTimeoutError extends Error {
  constructor(seconds: number) {
    super(`Rendering timed out after ${seconds}s.`);
    this.name = 'RenderTimeoutError';
  }
}

export class UnsupportedMediaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedMediaError';
  }
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    if (signal.reason instanceof RenderTimeoutError) {
      throw signal.reason;
    }
    throw new RenderCancelledError();
  }
}
